import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Grid, Stack, Typography, Button, Box, Link, Skeleton} from 'views/components';
import {WarningAmber} from 'views/icons';
import {Translate, translate, MarkdownViewer} from 'views/module';

import backend from 'utils/backend';

const daysOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const ViewRecurrence = (props) => {
  const [caregiverData, setCaregiverData] = React.useState(null);

  const onClose = props.onClose;
  const event = props.event;
  const recurrenceRn = props.recurrenceRn;
  const deleteRecurrence = props.deleteRecurrence;

  // Load caregiver data
  React.useEffect(() => {
    backend.caregiver.getCaregiver(event.caregiverData.caregiverRn, true)
        .then((e) => {
          setCaregiverData(e);
        })
        .catch(() => {
          console.error('Failed to load caregiver data');
        });
  }, [event]);

  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression="client.ClientDetail.Scheduling.ViewRecurrence" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} >
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.Title'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              {event.info.title}
            </Typography>
          </Stack>
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.Description'/>
            </Typography>
            <Box
              sx={{
                fontSize: '14px',
              }}
            >
              {
                !event.info.description.blob &&
                translate('client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.NoDescription')
              }
              {
                event.info.description.blob &&
                <MarkdownViewer
                  value={event.info.description.blob}
                  truncate={true}
                />
              }
            </Box>
          </Stack>
          <Stack>
            <Grid container>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.StartDate'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.recurrenceData.startDate}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.EndDate'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {
                      event.recurrenceData.endDate &&
                      event.recurrenceData.endDate
                    }
                    {
                      !event.recurrenceData.endDate &&
                      translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.Infinity')
                    }
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.Frequency'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.FrequencyData.${event.recurrenceData.freq}`)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.Interval'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.recurrenceData.interval}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.StartTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.recurrenceData.startTime}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.EndTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.recurrenceData.endTime}
                  </Typography>
                </Stack>
              </Grid>
              {
                event.recurrenceData.freq === 'WEEKLY' &&
                <Grid item xs={12}>
                  <Box >
                    {
                      daysOfWeek.map((day) => (
                        <Button
                          key={day}
                          variant={event.recurrenceData.byweekday.includes(day) ? 'contained' : 'outlined'}
                          sx={{
                            minWidth: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            fontSize: '16px',
                            padding: '0',
                            mr: 1,
                          }}
                        >
                          {translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.DayOfTheWeek.${day}`)}
                        </Button>
                      ))
                    }
                  </Box>
                </Grid>
              }
              {
                event.recurrenceData.freq === 'MONTHLY' &&
                <Grid item xs={6}>
                  <Stack>
                    <Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonthDay'/>
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                      }}
                    >
                      {event.recurrenceData.bymonthday.join(',')}
                    </Typography>
                  </Stack>
                </Grid>
              }
              {
                event.recurrenceData.freq === 'YEARLY' &&
                <>
                  <Grid item xs={6}>
                    <Stack>
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonth'/>
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                        }}
                      >
                        {event.recurrenceData.bymonth.join(',')}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack>
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonthDay'/>
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                        }}
                      >
                        {event.recurrenceData.bymonthday.join(',')}
                      </Typography>
                    </Stack>
                  </Grid>
                </>
              }
            </Grid>
          </Stack>
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Caregiver.CaregiverSearch'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              {caregiverData ? (
                <Link href={`/caregiver/${caregiverData.caregiverRn}`}>
                  {caregiverData.caregiverDetail.caregiverName}
                </Link>
              ) : (
                <Skeleton sx={{fontSize: '14px', width: '100px'}} />
              )}
            </Typography>
          </Stack>
          {
            event.status === 'DEACTIVATED' &&
            <Stack
              direction='row'
              spacing={1}
              sx={{
                backgroundColor: 'grey.200',
                borderRadius: '8px',
                color: 'warning.main',
                p: 1,
                py: 2,
              }}
            >
              <WarningAmber/>
              <Translate expression="client.ClientDetail.Scheduling.ThisReccurenceIsDeleted" />
            </Stack>
          }
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='generic.Close' />
        </Button>
        {
          event.status === 'ACTIVE' &&
          <Button color="error" variant="contained" onClick={() => deleteRecurrence(recurrenceRn, event)}>
            <Translate expression="client.ClientDetail.Scheduling.DeleteRecurrence" />
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
};

ViewRecurrence.propTypes = {
  recurrenceRn: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  deleteRecurrence: PropTypes.func.isRequired,
};

export default React.memo(ViewRecurrence);
